import { useCallback, useEffect, useRef, useState } from 'react';
import { getCalls, type CallEntry } from '../api';
import { VoicemailList, useVoicemails } from './VoicemailList';
import { VoicemailPlay } from './VoicemailPlay';
import { describeCall, groupRepeats, ownNumberText, type CallGroup } from '../phone/call-text';
import { callerOf, displayNumber, formatPhone } from '../phone/number';
import type { Phone } from '../phone/phone';
import { ArrowInIcon, ArrowOutIcon, MissedIcon, PhoneIcon } from './icons';
import { clockTime, listDayLabel, shownZoneNote } from '../time';

type Filter = 'all' | 'missed' | 'voicemail';

export function History({
  phone,
  canCall,
  onCallBack,
  severalNumbers = false,
  numberNames,
}: {
  phone: Phone;
  canCall: boolean;
  onCallBack: (n: string) => void;
  /** the user has several numbers: say which one each call used ("· from" outgoing, "· to" incoming) */
  severalNumbers?: boolean;
  /** Numbers PR 2: the user's names for their numbers */
  numberNames?: Record<string, string>;
}) {
  const [calls, setCalls] = useState<CallEntry[]>([]);
  const [next, setNext] = useState<number | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const req = useRef(0);
  const haveCalls = useRef(false);
  haveCalls.current = calls.length > 0;

  const loadFirst = useCallback(async (quiet = false) => {
    const id = ++req.current;
    if (!quiet) setState('loading');
    try {
      const page = await getCalls();
      if (id !== req.current) return;
      setCalls(page.calls);
      setNext(page.nextBefore);
      setState('ready');
      setError(null);
    } catch (err) {
      if (id !== req.current) return;
      if (quiet && haveCalls.current) return; // keep what we have
      setError((err as Error).message);
      setState('error');
    }
  }, []);

  const loadMore = async () => {
    if (!next || more) return;
    setMore(true);
    try {
      const page = await getCalls(next);
      setCalls((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        return [...prev, ...page.calls.filter((c) => !seen.has(c.id))];
      });
      setNext(page.nextBefore);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setMore(false);
    }
  };

  useEffect(() => {
    void loadFirst();
  }, [loadFirst]);

  useEffect(() => phone.onHistoryChange(() => void loadFirst(true)), [phone, loadFirst]);

  const vm = useVoicemails(phone);
  const heardLocally = (id: number) => {
    vm.markHeard(id);
    setCalls((prev) => prev.map((c) => (c.voicemail?.id === id ? { ...c, voicemail: { ...c.voicemail, heard: true } } : c)));
  };

  const groups = groupRepeats(calls);
  const shown = filter === 'missed' ? groups.filter((g) => g.call.status === 'missed') : groups;
  const missedCount = groups.filter((g) => g.call.status === 'missed').length;

  const zoneNote = shownZoneNote();

  return (
    <div className="history">
      <div className="history-head">
        <h2>Recent calls</h2>
        <div className="tabs" role="tablist" aria-label="Show">
          <button role="tab" type="button" aria-selected={filter === 'all'} onClick={() => setFilter('all')}>
            All
          </button>
          <button role="tab" type="button" aria-selected={filter === 'missed'} onClick={() => setFilter('missed')}>
            Missed{missedCount ? ` (${missedCount}${next ? '+' : ''})` : ''}
          </button>
          <button role="tab" type="button" aria-selected={filter === 'voicemail'} onClick={() => setFilter('voicemail')}>
            Voicemail
            {vm.unheard > 0 && (
              <span className="tab-badge" aria-label={`${vm.unheard} new`}>
                {vm.unheard}
              </span>
            )}
          </button>
        </div>
      </div>
      {zoneNote && <p className="history-zone">{zoneNote}</p>}

      {filter === 'voicemail' && (
        <VoicemailList
          vm={vm}
          canCall={canCall}
          onCallBack={onCallBack}
          onHeard={heardLocally}
          onDeleted={(id) => setCalls((prev) => prev.map((c) => (c.voicemail?.id === id ? { ...c, voicemail: null } : c)))}
          groupLabel={dayLabel}
          timeOf={timeOf}
          severalNumbers={severalNumbers}
          numberNames={numberNames}
        />
      )}

      {filter !== 'voicemail' && state === 'loading' && <p className="history-empty">Loading your calls…</p>}
      {filter !== 'voicemail' && state === 'error' && (
        <div className="history-empty">
          <p>Couldn’t load your calls. {error}</p>
          <button className="btn btn-small" type="button" onClick={() => void loadFirst()}>
            Try again
          </button>
        </div>
      )}
      {filter !== 'voicemail' && state === 'ready' && shown.length === 0 && (
        <p className="history-empty">
          {filter === 'missed'
            ? 'No missed calls.'
            : 'No calls yet. Calls you make and receive show up here, newest first.'}
        </p>
      )}

      {filter !== 'voicemail' && state === 'ready' && shown.length > 0 && (
        <ol className="calls">
          {groupByDay(shown).map(([day, items]) => (
            <li key={day} className="calls-day">
              <h3>{day}</h3>
              <ol>
                {items.map(({ call: c, count }) => {
                  const who = callerOf(c.number, c.name);
                  const ownNum = c.direction === 'outbound' ? c.fromNumber : c.toNumber;
                  const own = ownNumberText(severalNumbers, c.direction, ownNum, numberNames);
                  return (
                  <li key={c.id} className={`call call-${c.status}`}>
                    <span className="call-dir" aria-hidden>
                      {c.status === 'missed' ? <MissedIcon /> : c.direction === 'outbound' ? <ArrowOutIcon /> : <ArrowInIcon />}
                    </span>
                    <span className="call-main">
                      <span className="call-number">
                        <span className="call-who">{who.name ?? (displayNumber(who.number) || 'Unknown caller')}</span>
                        {count > 1 && (
                          <span className="call-count" aria-label={`${count} calls`} title={`${count} calls within a few minutes`}>
                            ({count})
                          </span>
                        )}
                      </span>
                      <span className="call-meta">
                        {count > 1 && <span className="call-count-meta">{count} calls · </span>}
                        {who.name && who.number ? `${displayNumber(who.number)} · ` : ''}
                        {describeCall(c)}
                        {own ? <span className="call-from" title={ownNum ? formatPhone(ownNum) : undefined}>{own}</span> : null}
                      </span>
                      {c.voicemail && (
                        <VoicemailPlay
                          id={c.voicemail.id}
                          durationSec={c.voicemail.durationSec}
                          heard={c.voicemail.heard}
                          onHeard={heardLocally}
                        />
                      )}
                    </span>
                    <time className="call-time" dateTime={c.startedAt}>
                      {timeOf(c.startedAt)}
                    </time>
                    <button
                      type="button"
                      className="icon-btn call-again"
                      disabled={!canCall || !c.number}
                      aria-label={`Call ${displayNumber(c.number)}`}
                      title={`Call ${displayNumber(c.number)}`}
                      onClick={() => onCallBack(c.number)}
                    >
                      <PhoneIcon size={18} />
                    </button>
                  </li>
                  );
                })}
              </ol>
            </li>
          ))}
        </ol>
      )}

      {filter !== 'voicemail' && state === 'ready' && next && (
        <button className="btn btn-quiet btn-block" type="button" onClick={() => void loadMore()} disabled={more}>
          {more ? 'Loading…' : 'Show older calls'}
        </button>
      )}
    </div>
  );
}

// time-zone PR: times and day headings follow the zone picked in Settings (time.ts)
const timeOf = (iso: string): string => clockTime(iso);
const dayLabel = (d: Date): string => listDayLabel(d);

function groupByDay(groups: Array<CallGroup<CallEntry>>): Array<[string, Array<CallGroup<CallEntry>>]> {
  const out: Array<[string, Array<CallGroup<CallEntry>>]> = [];
  for (const g of groups) {
    const label = dayLabel(new Date(g.call.startedAt));
    const last = out[out.length - 1];
    if (last && last[0] === label) last[1].push(g);
    else out.push([label, [g]]);
  }
  return out;
}
